import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Shadows() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🌑 Advanced CSS: Shadows</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Shadows in CSS are not just for depth—they can be used for{" "}
        <b>
          neon effects, glowing buttons, 3D illusions, layered cards, and UI
          aesthetics
        </b>
        . In advanced CSS, we use <code>text-shadow</code> and{" "}
        <code>box-shadow</code> in creative ways.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>text-shadow</code> → For glowing or embossed text effects.
          </li>
          <li>
            <code>box-shadow</code> → For card depth, floating elements, UI
            layers.
          </li>
          <li>
            Supports <b>multiple shadows</b> separated by commas.
          </li>
          <li>
            Use <code>inset</code> for inner shadows.
          </li>
          <li>Combine with gradients for advanced glowing/neon effects.</li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Syntax</h2>
        <CodeCopyBlock
          code={`/* Box Shadow */
box-shadow: offset-x offset-y blur-radius spread-radius color;

/* Text Shadow */
text-shadow: offset-x offset-y blur-radius color;

/* Multiple shadows */
box-shadow: 2px 2px 5px rgba(0,0,0,0.3),
            -2px -2px 5px rgba(0,0,0,0.1);`}
        />
      </div>

      {/* Basic Box Shadow */}
      <div className="tutorial-section">
        <h2 className="section-title">📦 Basic Box Shadow</h2>
        <div
          style={{
            background: "#fff",
            padding: "2rem",
            borderRadius: "10px",
            boxShadow: "4px 6px 12px rgba(0,0,0,0.2)",
            width: "200px",
            textAlign: "center",
            marginBottom: "1rem",
          }}
        >
          Card with Shadow
        </div>
        <CodeCopyBlock
          code={`div {
  box-shadow: 4px 6px 12px rgba(0,0,0,0.2);
}`}
        />
      </div>

      {/* Inset Shadow */}
      <div className="tutorial-section">
        <h2 className="section-title">🔲 Inset Shadow</h2>
        <div
          style={{
            background: "#e5e7eb",
            padding: "2rem",
            borderRadius: "10px",
            boxShadow: "inset 4px 4px 8px rgba(0,0,0,0.3)",
            width: "200px",
            textAlign: "center",
            marginBottom: "1rem",
          }}
        >
          Inner Shadow
        </div>
        <CodeCopyBlock
          code={`div {
  box-shadow: inset 4px 4px 8px rgba(0,0,0,0.3);
}`}
        />
      </div>

      {/* Multiple Shadows */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Multiple Shadows</h2>
        <div
          style={{
            background: "#fff",
            padding: "2rem",
            borderRadius: "10px",
            boxShadow:
              "2px 2px 6px rgba(0,0,0,0.2), -2px -2px 6px rgba(0,0,0,0.1)",
            width: "200px",
            textAlign: "center",
            marginBottom: "1rem",
          }}
        >
          Layered Shadows
        </div>
        <CodeCopyBlock
          code={`div {
  box-shadow: 
    2px 2px 6px rgba(0,0,0,0.2),
    -2px -2px 6px rgba(0,0,0,0.1);
}`}
        />
      </div>

      {/* Glowing Button */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Glowing Shadow Button</h2>
        <button
          style={{
            padding: "12px 24px",
            fontSize: "1rem",
            borderRadius: "30px",
            border: "none",
            background: "#3b82f6",
            color: "white",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 0 10px #3b82f6, 0 0 20px #60a5fa, 0 0 30px #93c5fd",
            marginBottom: "1rem",
          }}
        >
          Glow Me
        </button>
        <CodeCopyBlock
          code={`button {
  background: #3b82f6;
  color: #fff;
  border-radius: 30px;
  box-shadow: 
    0 0 10px #3b82f6,
    0 0 20px #60a5fa,
    0 0 30px #93c5fd;
}`}
        />
      </div>

      {/* Text Shadow Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🖋️ Text Shadow</h2>
        <h2
          style={{
            fontSize: "2rem",
            color: "#1e3a8a",
            textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            marginBottom: "1rem",
          }}
        >
          Stylish Text
        </h2>
        <CodeCopyBlock
          code={`h2 {
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}`}
        />
      </div>

      {/* Neon Text */}
      <div className="tutorial-section">
        <h2 className="section-title">🌟 Neon Text</h2>
        <h2
          style={{
            fontSize: "2rem",
            color: "#10b981",
            textShadow: "0 0 5px #10b981, 0 0 10px #34d399, 0 0 20px #6ee7b7",
            marginBottom: "1rem",
          }}
        >
          Neon Effect
        </h2>
        <CodeCopyBlock
          code={`h2 {
  color: #10b981;
  text-shadow: 
    0 0 5px #10b981,
    0 0 10px #34d399,
    0 0 20px #6ee7b7;
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <b>3D shadow illusions</b> by stacking multiple
            shadows.
          </li>
          <li>
            Create <b>neumorphism effects</b> (soft UI) using light & dark
            shadows.
          </li>
          <li>
            Animate shadows with <code>@keyframes</code> for glowing pulse
            effects.
          </li>
          <li>
            Mix <code>box-shadow</code> with <b>clip-path</b> for unique UI
            elements.
          </li>
          <li>
            Use <code>filter: drop-shadow()</code> for SVG and images.
          </li>
        </ul>
      </div>
    </div>
  );
}
